import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import InfoIcon from '../../assets/svg/Feather/Info';
import ghAnalytics from '../../lib/analytics';
import { COLORS } from '../../lib/constants';
import styles from './InfoButton.module.scss';

function SectionTooltip({ text, visible, tooltipRef, feedbackUrl }) {
  return (
    <div ref={tooltipRef} className={`${styles.sectionTooltip} ${visible ? styles.sectionTooltipVisible : ''}`}>
      <p className={styles.sectionTooltipText}>{text}</p>
      <a
        href={feedbackUrl || '/contact'}
        className={styles.sectionTooltipFeedback}
        target='_blank'
        rel='noopener noreferrer'
      >
        Share feedback
      </a>
    </div>
  );
}

SectionTooltip.propTypes = {
  text: PropTypes.string,
  visible: PropTypes.bool,
  tooltipRef: PropTypes.object,
  feedbackUrl: PropTypes.string,
};

/**
 * An "i" icon button that shows a floating tooltip beneath it when clicked.
 * Only one tooltip should be open at a time — controlled via `openTooltip` / `onToggle`.
 *
 * Props:
 *   tooltipKey   – unique string identifying this tooltip (e.g. 'active_layers')
 *   tooltipText  – the text to display inside the tooltip
 *   openTooltip  – the currently open tooltip key (or null)
 *   onToggle     – called with (tooltipKey | null) to open/close
 *   feedbackUrl  – optional override for the "Share feedback" link (default: '/contact')
 */
function InfoButton({ tooltipKey, tooltipText, openTooltip, onToggle, feedbackUrl }) {
  const isOpen = openTooltip === tooltipKey;
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      const insideButton = buttonRef.current && buttonRef.current.contains(e.target);
      const insideTooltip = tooltipRef.current && tooltipRef.current.contains(e.target);
      if (!insideButton && !insideTooltip) {
        onToggle(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isOpen, onToggle]);

  const handleClick = () => {
    if (isOpen) {
      onToggle(null);
    } else {
      ghAnalytics.logTooltipShown(tooltipKey);
      onToggle(tooltipKey);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        className={`${styles.infoButton} ${isOpen ? styles.infoButtonActive : ''}`}
        onClick={handleClick}
        aria-label='More information'
      >
        <InfoIcon color={isOpen ? COLORS.ghOrange : '#707070'} width={14} />
      </button>
      <SectionTooltip
        text={tooltipText}
        visible={isOpen}
        tooltipRef={tooltipRef}
        feedbackUrl={feedbackUrl}
      />
    </>
  );
}

InfoButton.propTypes = {
  tooltipKey: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
  openTooltip: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  feedbackUrl: PropTypes.string,
};

export default InfoButton;
